import * as React from "react";
import {cx} from "emotion";

import {cs}                     from "@Common/react/chain-services";
import {consumeContext}         from "@Common/react/context";
import {Select}                 from "@Common/ui-components/heavy/common/design2/select/select";
import {US_STATES}              from "@Common/ui-components/heavy/common/us-states";
import {CA_PROVINCES}           from "@Common/ui-components/heavy/common/canada-provinces";
import {UseState}               from "@Common/react/use-state";
import {FloatingInput}          from "@Xpresso/routes/account/common/floating-labels/floating-input";
import './address-edit.scss';

export const AddressEdit = ({branch, reload, loading, officesReload=null, showHeader=false, addressCard=false, readOnly=false}) => cs(
    consumeContext("apis"),
    ["showInput", ({}, next) => UseState({
        next,
        initValue: false
    })],
    ["addressUpdate", ({}, next) => UseState({
        next,
        defaultValue: addressUpdateDefault(branch)
    })],
    ["uploading", ({}, next) => UseState({next})],
    ({addressUpdate, apis, showInput, uploading}) => (
        <div className="address-edit-dznts3">
            {showHeader &&
                <div className="name-office" xd-qa-type="label" xd-qa-name="Office Name">
                    {loading ? "Loading office..." : branch.branchName}
                </div>
            }
            <div className="section">
                <div className={cx("header", {card: addressCard})}>
                    <div className="title">
                        Office Address
                    </div>
                    {!readOnly &&
                        <a className="edit" xd-qa-type="link" xd-qa-name="Edit Address" 
                            onClick={() => {
                                const toggle = !showInput.value
                                showInput.onChange(toggle)
                                addressUpdate.onChange(addressUpdateDefault(branch))
                            }}>
                            {showInput.value ? "Cancel" : "Edit"}
                        </a>
                    }
                </div>
            </div>
            <div className={cx("address-edit", {visible: showInput.value})} >
                {addressEditFields.map((field, i) =>
                    <React.Fragment key={i}>
                        <FloatingInput 
                            classContainer="input-card"
                            classLabel="label"
                            props={propsBranchField(addressUpdate, field)}
                            required={field.required}
                            textLabel={field.label}
                        />
                    </React.Fragment>
                )}

                {(branch?.branchCountry === "US" || branch?.branchCountry === "CA") ? (
                    <div className="container-select">
                        {Select({
                            list: branch?.branchCountry === "CA" ? CA_PROVINCES : US_STATES,
                            itemToLabel: (state) => state.name,
                            onChange: (state) => {
                                const address = addressUpdate.value
                                address.branchState = state.abbreviation
                                addressUpdate?.onChange(address)
                            },
                            autofill: {
                                getItem: (str) => (
                                    branch?.branchCountry === "CA" ? 
                                    (
                                        CA_PROVINCES.find((province) => province.abbreviation === str) ??
                                        CA_PROVINCES.find((province) => province.name.toLowerCase() === str.toLowerCase())
                                    ) : (
                                        US_STATES.find((state) => state.abbreviation === str) ??
                                        US_STATES.find((state) => state.name.toLowerCase() === str.toLowerCase())
                                    )
                                ),
                                name: "state",
                            },
                            isSelected: (state) => (addressUpdate?.value?.branchState === state.abbreviation),
                            xdQaName: "State",
                            className: addressUpdate?.value?.branchState ? "valid" : null
                        })}
                        <label className="label" >State</label>              
                        <div className="required">*</div>
                    </div>
                    ) : (
                    <FloatingInput 
                        classContainer="input-card"
                        classLabel="label"
                        props={propsBranchField(addressUpdate, branchState)}
                        required={false}
                        textLabel={branchState.label}
                    />
                    )
                }
                <FloatingInput 
                    classContainer="input-card"
                    classLabel="label"
                    props={propsBranchField(addressUpdate, branchZip)}
                    required={(branch?.branchCountry === "US" || branch?.branchCountry === "CA")}
                    textLabel={branchZip.label}
                />
                <div 
                    className={cx("branch-save", {disabled: !saveButtonEnabled(uploading, addressUpdate, branch)})}
                    onClick={async () => {
                        if(!saveButtonEnabled(uploading, addressUpdate, branch)) {
                            return
                        }

                        uploading.onChange(true)
                        const result = await apis.next.branch.updateBasicInfo(addressUpdate.value)
                        if (result?.errorMessage) {
                            alert(result.errorMessage)
                        }
                        if (result?.errorInfo) {
                            alert(result.errorInfo)
                        }
                        await reload()
                        if(officesReload) {
                            officesReload()
                        }
                        uploading.onChange(false)
                        showInput.onChange(false)
                    }}  
                >
                    Save
                </div>
            </div>
            <section className={cx("address", {card: addressCard}, {hidden: showInput.value})}>
                <span xd-qa-type="label" xd-qa-name="Street Address">
                    {loading ? textPlaceholder : [branch.branchAddress1]},
                </span>
                    {branch?.branchAddress2 &&
                        <span xd-qa-type="label" xd-qa-name="Street Address">
                            {loading ? textPlaceholder : [branch.branchAddress2]},
                        </span>
                    }
                <span xd-qa-type="label" xd-qa-name="City State Zip">
                    {loading ? textPlaceholder : `${branch.branchCity}, ${branch.branchState} ${branch.branchZip}`}
                </span>
            </section>            
        </div>
    )
)

const addressUpdateDefault = (branch) => {
    return {
        idBranch: branch?.idBranch,
        branchAddress1: branch?.branchAddress1, 
        branchAddress2: branch?.branchAddress2,
        branchCity: branch?.branchCity,
        branchState: branch?.branchState,
        branchZip: branch?.branchZip
    }
}

const addressEditFields = [
    {
        key: "branchAddress1",
        label: "Address 1",
        "xd-qa-name": "branchAddress1",
        maxLength: 50,
        required: true
    },
    {
        key: "branchAddress2",
        label: "Address 2",
        "xd-qa-name": "branchAddress2",
        maxLength: 50,
        required: false
    },
    {
        key: "branchCity",
        label: "City",
        "xd-qa-name": "city",
        maxLength: 25,
        required: true
    }
]

const branchZip = {
    key: "branchZip",
    label: "Zip",
    "xd-qa-name": "zip",
    maxLength: 10,
}

const branchState = {
    key: "branchState",
    label: "State",
    "xd-qa-name": "state",
    maxLength: 20,
}

const propsBranchField = (addressUpdate, field) => ({
    value: addressUpdate?.value?.[`${field.key}`] ?? textPlaceholder,
    onChange: (e) => {
        const address = addressUpdate.value
        address[`${field.key}`] = e.target.value
        addressUpdate?.onChange(address)
    },
    className: "value",
    maxLength: field["maxLength"],
    "xd-qa-type": "textbox",
    "xd-qa-name": field["xd-qa-name"],
    autoComplete: "new-password",
})

const saveButtonEnabled = (uploading, addressUpdate, branch) => {
    if(uploading.value) {
        return false
    }

    if(JSON.stringify(addressUpdate?.value) === JSON.stringify(addressUpdateDefault(branch))) {
        return false
    }

    if(!addressUpdate?.value?.branchAddress1 || !addressUpdate?.value?.branchCity) {
        return false
    }
    
    if(branch?.branchCountry === "US" || branch?.branchCountry === "CA") {
        if(!addressUpdate?.value?.branchState || !addressUpdate?.value?.branchZip) {
             return false
        }
    }

    return true
}

const textPlaceholder = "..."